import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Layers,
  Search,
  Database,
  FileText,
  HardDrive,
  Activity,
  Zap,
  Info,
  Loader2,
  ArrowUpDown,
  Filter,
  X,
  FileCode,
  BarChart2,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Cell,
} from 'recharts';
import baseapi from '../../../services/baseapi';

interface MongoCollectionsView2Props {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

interface CollectionInventoryItem {
  collection: string;
  database: string;
  documents: number;
  data_size_mb: number;
  storage_size_mb: number;
  index_size_mb: number;
  avg_document_size_bytes: number;
  index_count: number;
  reads_per_sec: number | null;
  inserts_per_sec: number | null;
  updates_per_sec: number | null;
  deletes_per_sec: number | null;
  commands_per_sec: number | null;
  operations_per_sec: number | null;
  is_capped: boolean;
  is_system: boolean;
}

interface MongoCollectionsApiResponse {
  database: string | null;
  overview?: {
    collection_count?: number;
    document_count?: number;
    data_size_mb?: number;
    storage_size_mb?: number;
    index_size_mb?: number;
    avg_document_size_bytes?: number;
  };
  activity?: {
    reads_per_sec?: number | null;
    inserts_per_sec?: number | null;
    updates_per_sec?: number | null;
    deletes_per_sec?: number | null;
    commands_per_sec?: number | null;
  };
  top_collections_by_size?: Array<{
    collection: string;
    data_size_mb: number;
    storage_size_mb: number;
  }>;
  top_collections_by_activity?: Array<{
    collection: string;
    operations_per_sec: number;
  }>;
  inventory?: CollectionInventoryItem[];
  rate_window_seconds?: number;
}

const fmtSize = (mb: number | null | undefined): string => {
  if (mb === null || mb === undefined || isNaN(mb)) return '0 MB';
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  if (mb > 0) return `${(mb * 1024).toFixed(1)} KB`;
  return '0 MB';
};

const fmtCount = (n: number | null | undefined): string => {
  if (n === null || n === undefined || isNaN(n)) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const fmtBytes = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || isNaN(bytes)) return '0 B';
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const ChartTooltip = ({ active, payload, label, unit }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[130px] z-50">
        {label !== undefined && label !== null && label !== "" && (
          <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
            {label}
          </div>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color || entry.stroke || entry.fill || "#f97316" }}
              />
              {entry.name || entry.dataKey}
            </span>
            <span className="font-bold text-gray-900 dark:text-white font-mono">
              {typeof entry.value === 'number' ? entry.value.toLocaleString() : (entry.value ?? '-')}
              {unit ? ` ${unit}` : ''}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const MongoCollectionsView2: React.FC<MongoCollectionsView2Props> = ({
  activeCluster,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<MongoCollectionsApiResponse | null>(null);
  const [notes, setNotes] = useState<Record<string, string> | null>(null);

  // Filters & Sorting
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedDb, setSelectedDb] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'user' | 'system' | 'capped'>('all');
  const [sortField, setSortField] = useState<keyof CollectionInventoryItem>('storage_size_mb');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedCollectionDetail, setSelectedCollectionDetail] = useState<CollectionInventoryItem | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const params: any = {
      cluster_id: activeCluster,
      
    };
    if (clientId) params.client_id = clientId;

    try {
      const res = await baseapi.get('/db_monitor_dashboard/mongodb/collections', { params });
      const raw = res.data;
      if (raw?.status && raw?.data) {
        setData(raw.data);
        if (raw.notes) setNotes(raw.notes);
      } else if (raw?.overview || raw?.inventory) {
        setData(raw);
        if (raw.notes) setNotes(raw.notes);
      } else {
        setData(null);
      }
    } catch (err) {
      console.warn('MongoCollectionsView2 fetch error:', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const overview = data?.overview;
  const activity = data?.activity;
  const inventory = useMemo(() => data?.inventory || [], [data?.inventory]);
  const topSize = data?.top_collections_by_size || [];
  const topActivity = data?.top_collections_by_activity || [];

  // Extract unique databases
  const availableDatabases = useMemo(() => {
    const dbs = new Set<string>();
    inventory.forEach((item) => {
      if (item.database) dbs.add(item.database);
    });
    return Array.from(dbs).sort();
  }, [inventory]);

  // Filtered Inventory
  const filteredInventory = useMemo(() => {
    return inventory.filter((item) => {
      const matchesSearch =
        !searchQuery.trim() ||
        item.collection.toLowerCase().includes(searchQuery.toLowerCase().trim()) ||
        item.database.toLowerCase().includes(searchQuery.toLowerCase().trim());

      const matchesDb =
        selectedDb === 'all' || item.database.toLowerCase() === selectedDb.toLowerCase();

      let matchesType = true;
      if (typeFilter === 'user') matchesType = !item.is_system;
      if (typeFilter === 'system') matchesType = item.is_system;
      if (typeFilter === 'capped') matchesType = item.is_capped;

      return matchesSearch && matchesDb && matchesType;
    });
  }, [inventory, searchQuery, selectedDb, typeFilter]);

  // Sorted Inventory
  const sortedInventory = useMemo(() => {
    return [...filteredInventory].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const numA = Number(aVal);
      const numB = Number(bVal);
      return sortOrder === 'asc' ? numA - numB : numB - numA;
    });
  }, [filteredInventory, sortField, sortOrder]);

  const handleSort = (field: keyof CollectionInventoryItem) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Compression Ratio Calculation
  const compressionSavingsPct = useMemo(() => {
    const dataSize = overview?.data_size_mb || 0;
    const storageSize = overview?.storage_size_mb || 0;
    if (dataSize <= 0 || storageSize <= 0) return 0;
    const saved = ((dataSize - storageSize) / dataSize) * 100;
    return Math.max(0, Math.round(saved));
  }, [overview]);

  // Total CRUD ops per sec
  const totalOpsPerSec = useMemo(() => {
    if (!activity) return 0;
    const reads = activity.reads_per_sec || 0;
    const inserts = activity.inserts_per_sec || 0;
    const updates = activity.updates_per_sec || 0;
    const deletes = activity.deletes_per_sec || 0;
    const commands = activity.commands_per_sec || 0;
    return Number((reads + inserts + updates + deletes + commands).toFixed(2));
  }, [activity]);

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[400px]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* View Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-800 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900 dark:text-white tracking-wide">
                MongoDB Collections & Storage
              </h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30 font-mono">
                WiredTiger Engine
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Live collection inventory, uncompressed data vs. compressed disk footprint, index allocations, and rate activity.
            </p>
          </div>
        </div>

        {data?.rate_window_seconds && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700/80 rounded-lg text-xs font-mono text-gray-700 dark:text-gray-300">
            <Activity className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
            <span>Rate Window: <strong className="text-gray-900 dark:text-white">{data.rate_window_seconds}s</strong></span>
          </div>
        )}
      </div>

      {/* KPI Overview Telemetry Cards (Compact Grid) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        {/* COLLECTIONS */}
        <div className="bg-white dark:bg-gradient-to-br dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-emerald-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <Layers className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />
              Collections
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-gray-900 dark:text-white tracking-tight my-0.5">
            {overview?.collection_count ?? inventory.length}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800/80 truncate">
            <span>{inventory.filter((i) => !i.is_system).length} User</span> • <span>{inventory.filter((i) => i.is_system).length} System</span>
          </div>
        </div>

        {/* DOCUMENTS */}
        <div className="bg-white dark:bg-gradient-to-br dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-blue-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <FileText className="w-3 h-3 text-blue-500 dark:text-blue-400" />
              Documents
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-blue-600 dark:text-blue-400 tracking-tight my-0.5">
            {fmtCount(overview?.document_count)}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800/80 truncate">
            Avg: <strong className="text-gray-800 dark:text-gray-200 font-mono">{fmtBytes(overview?.avg_document_size_bytes)}</strong>
          </div>
        </div>

        {/* UNCOMPRESSED DATA SIZE */}
        <div className="bg-white dark:bg-gradient-to-br dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-amber-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <HardDrive className="w-3 h-3 text-amber-500 dark:text-amber-400" />
              Data Size
            </span>
            <span className="text-[9px] font-mono text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 px-1 py-0.2 rounded font-bold">Raw</span>
          </div>
          <div className="text-xl font-bold font-mono text-amber-600 dark:text-amber-400 tracking-tight my-0.5">
            {fmtSize(overview?.data_size_mb)}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800/80 truncate">
            Uncompressed data
          </div>
        </div>

        {/* STORAGE SIZE (DISK) */}
        <div className="bg-white dark:bg-gradient-to-br dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-purple-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <HardDrive className="w-3 h-3 text-purple-500 dark:text-purple-400" />
              Disk Storage
            </span>
            {compressionSavingsPct > 0 && (
              <span className="text-[9px] font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-1 py-0.2 rounded font-bold">
                -{compressionSavingsPct}%
              </span>
            )}
          </div>
          <div className="text-xl font-bold font-mono text-purple-600 dark:text-purple-400 tracking-tight my-0.5">
            {fmtSize(overview?.storage_size_mb)}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800/80 truncate">
            WiredTiger disk hold
          </div>
        </div>

        {/* INDEX SIZE */}
        <div className="bg-white dark:bg-gradient-to-br dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-cyan-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <Database className="w-3 h-3 text-cyan-500 dark:text-cyan-400" />
              Index Size
            </span>
          </div>
          <div className="text-xl font-bold font-mono text-cyan-600 dark:text-cyan-400 tracking-tight my-0.5">
            {fmtSize(overview?.index_size_mb)}
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800/80 truncate">
            Disk index footprint
          </div>
        </div>

        {/* TOTAL OP RATE */}
        <div className="bg-white dark:bg-gradient-to-br dark:from-gray-900/90 dark:to-gray-900/60 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3 shadow-sm hover:border-rose-500/30 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <Zap className="w-3 h-3 text-rose-500 dark:text-rose-400" />
              Op Rate
            </span>
            <span className="text-[9px] font-mono text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10 px-1 py-0.2 rounded font-bold">Live</span>
          </div>
          <div className="text-xl font-bold font-mono text-rose-600 dark:text-rose-400 tracking-tight my-0.5">
            {totalOpsPerSec} <span className="text-xs font-normal">ops/s</span>
          </div>
          <div className="text-[10px] text-gray-500 dark:text-gray-400 pt-1 border-t border-gray-100 dark:border-gray-800/80 truncate">
            Cmds: <strong className="text-gray-800 dark:text-gray-200 font-mono">{activity?.commands_per_sec ?? 0}/s</strong>
          </div>
        </div>
      </div>

      {/* Real-Time Activity CRUD Telemetry Bar */}
      {activity && (
        <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/60 rounded-lg p-2.5 px-3.5 flex flex-wrap items-center justify-between gap-3 text-xs font-mono">
          <div className="flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
            <span className="text-gray-700 dark:text-gray-300 font-sans font-semibold text-[11px] uppercase tracking-wider">
              Operation Throughput Breakdown:
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 bg-white dark:bg-gray-900/80 px-2.5 py-1 rounded border border-gray-200 dark:border-gray-700/80">
              <span className="text-gray-500 dark:text-gray-400 font-sans text-[11px]">Reads:</span>
              <span className="text-emerald-600 dark:text-emerald-400 font-bold">{activity.reads_per_sec ?? 0} /s</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white dark:bg-gray-900/80 px-2.5 py-1 rounded border border-gray-200 dark:border-gray-700/80">
              <span className="text-gray-500 dark:text-gray-400 font-sans text-[11px]">Inserts:</span>
              <span className="text-blue-600 dark:text-blue-400 font-bold">{activity.inserts_per_sec ?? 0} /s</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white dark:bg-gray-900/80 px-2.5 py-1 rounded border border-gray-200 dark:border-gray-700/80">
              <span className="text-gray-500 dark:text-gray-400 font-sans text-[11px]">Updates:</span>
              <span className="text-amber-600 dark:text-amber-400 font-bold">{activity.updates_per_sec ?? 0} /s</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white dark:bg-gray-900/80 px-2.5 py-1 rounded border border-gray-200 dark:border-gray-700/80">
              <span className="text-gray-500 dark:text-gray-400 font-sans text-[11px]">Deletes:</span>
              <span className="text-rose-600 dark:text-rose-400 font-bold">{activity.deletes_per_sec ?? 0} /s</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white dark:bg-gray-900/80 px-2.5 py-1 rounded border border-gray-200 dark:border-gray-700/80">
              <span className="text-gray-500 dark:text-gray-400 font-sans text-[11px]">Commands:</span>
              <span className="text-purple-600 dark:text-purple-400 font-bold">{activity.commands_per_sec ?? 0} /s</span>
            </div>
          </div>
        </div>
      )}

      {/* Top Collections Visual Charts (Size & Activity) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
        {/* Top Collections by Size */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-xl">
          <div className="flex items-center justify-between pb-2.5 mb-2 border-b border-gray-200 dark:border-gray-700/50">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-orange-500 dark:text-orange-400" />
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Top Collections by Size (MB)
              </h4>
            </div>
            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">Uncompressed vs. Disk Storage</span>
          </div>

          <div className="h-52 w-full flex items-center justify-center">
            {topSize.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topSize} margin={{ top: 10, right: 10, left: -15, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.25} />
                  <XAxis
                    dataKey="collection"
                    stroke="#9ca3af"
                    fontSize={10}
                    tickLine={false}
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                  />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <Tooltip
                    content={<ChartTooltip unit="MB" />}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Legend
                    verticalAlign="top"
                    height={30}
                    formatter={(val) => <span className="text-xs text-gray-700 dark:text-gray-300">{val}</span>}
                  />
                  <Bar dataKey="data_size_mb" name="Data Size (Raw)" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="storage_size_mb" name="Storage Size (Disk)" fill="#a855f7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic">No size telemetry available</div>
            )}
          </div>
        </div>

        {/* Top Collections by Activity */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-3.5 shadow-sm dark:shadow-xl">
          <div className="flex items-center justify-between pb-2.5 mb-2 border-b border-gray-200 dark:border-gray-700/50">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Top Collections by Activity (ops/sec)
              </h4>
            </div>
            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">Operations Frequency</span>
          </div>

          <div className="h-52 w-full flex items-center justify-center">
            {topActivity.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topActivity} margin={{ top: 10, right: 10, left: -15, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.25} />
                  <XAxis
                    dataKey="collection"
                    stroke="#9ca3af"
                    fontSize={10}
                    tickLine={false}
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                  />
                  <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} />
                  <Tooltip
                    content={<ChartTooltip unit="ops/s" />}
                    wrapperStyle={{ zIndex: 1000 }}
                  />
                  <Bar dataKey="operations_per_sec" name="Ops / sec" fill="#10b981" radius={[4, 4, 0, 0]}>
                    {topActivity.map((_, idx) => (
                      <Cell key={`cell-${idx}`} fill={idx === 0 ? '#10b981' : '#3b82f6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-xs text-gray-500 dark:text-gray-400 italic">No activity rate telemetry available</div>
            )}
          </div>
        </div>
      </div>

      {/* Collection Inventory Table Section */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-emerald-600 text-white shadow-md">
              <Layers className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Collection Inventory</h3>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30 font-mono">
                  {sortedInventory.length} {sortedInventory.length === 1 ? 'collection' : 'collections'}
                </span>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                Detailed collection storage allocations, documents, and CRUD rates
              </p>
            </div>
          </div>

          {/* Controls: Search, DB Filter, Type Filter */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Database Filter */}
            {availableDatabases.length > 0 && (
              <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 shadow-sm">
                <Filter className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                <select
                  value={selectedDb}
                  onChange={(e) => setSelectedDb(e.target.value)}
                  className="bg-transparent text-xs text-gray-800 dark:text-white focus:outline-none cursor-pointer pr-1 max-w-[140px] truncate"
                >
                  <option value="all" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white">All Databases</option>
                  {availableDatabases.map((db) => (
                    <option key={db} value={db} className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
                      {db}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Type Filter Tabs */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-900/90 p-1 rounded-lg border border-gray-200 dark:border-gray-700/80">
              <button
                onClick={() => setTypeFilter('all')}
                className={`px-2 py-1 text-[11px] font-medium rounded transition-all cursor-pointer ${
                  typeFilter === 'all'
                    ? 'bg-emerald-600 text-white font-bold shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setTypeFilter('user')}
                className={`px-2 py-1 text-[11px] font-medium rounded transition-all cursor-pointer ${
                  typeFilter === 'user'
                    ? 'bg-blue-600 text-white font-bold shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                User
              </button>
              <button
                onClick={() => setTypeFilter('system')}
                className={`px-2 py-1 text-[11px] font-medium rounded transition-all cursor-pointer ${
                  typeFilter === 'system'
                    ? 'bg-amber-600 text-white font-bold shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                System
              </button>
              <button
                onClick={() => setTypeFilter('capped')}
                className={`px-2 py-1 text-[11px] font-medium rounded transition-all cursor-pointer ${
                  typeFilter === 'capped'
                    ? 'bg-purple-600 text-white font-bold shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Capped
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search collection..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500/50 w-44 h-8 shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-white"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-[460px] border border-gray-200 dark:border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                <th
                  onClick={() => handleSort('collection')}
                  className="py-2.5 px-3 bg-gray-50 dark:bg-gray-900 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Collection</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('database')}
                  className="py-2.5 px-3 bg-gray-50 dark:bg-gray-900 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center gap-1">
                    <span>Database</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('documents')}
                  className="py-2.5 px-3 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Documents</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('data_size_mb')}
                  className="py-2.5 px-3 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Data Size</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('storage_size_mb')}
                  className="py-2.5 px-3 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Disk Storage</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('index_size_mb')}
                  className="py-2.5 px-3 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Index Size</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('avg_document_size_bytes')}
                  className="py-2.5 px-3 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Avg Doc Size</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th
                  onClick={() => handleSort('operations_per_sec')}
                  className="py-2.5 px-3 text-right bg-gray-50 dark:bg-gray-900 cursor-pointer hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  <div className="flex items-center justify-end gap-1">
                    <span>Ops / Sec</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th className="py-2.5 px-3 text-center bg-gray-50 dark:bg-gray-900">Flags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/40 font-mono text-xs">
              {sortedInventory.length > 0 ? (
                sortedInventory.map((item, idx) => {
                  return (
                    <tr
                      key={`${item.database}-${item.collection}-${idx}`}
                      onClick={() => setSelectedCollectionDetail(item)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer transition-colors group"
                      title="Click to view full collection details"
                    >
                      <td className="py-2.5 px-3 font-semibold text-gray-900 dark:text-white">
                        <div className="flex items-center gap-2">
                          <FileCode className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400 shrink-0" />
                          <span className="truncate group-hover:text-emerald-600 dark:group-hover:text-emerald-300 transition-colors">
                            {item.collection}
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-gray-700 dark:text-gray-300 font-sans">
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-[11px] border border-gray-200 dark:border-gray-700/70">
                          {item.database}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right text-blue-600 dark:text-blue-400 font-bold">
                        {item.documents.toLocaleString()}
                      </td>
                      <td className="py-2.5 px-3 text-right text-amber-600 dark:text-amber-400 font-semibold">
                        {fmtSize(item.data_size_mb)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-purple-600 dark:text-purple-400 font-bold">
                        {fmtSize(item.storage_size_mb)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-cyan-600 dark:text-cyan-400">
                        <div className="flex items-center justify-end gap-1">
                          <span>{fmtSize(item.index_size_mb)}</span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">({item.index_count} idx)</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right text-gray-600 dark:text-gray-300 font-sans text-[11px]">
                        {fmtBytes(item.avg_document_size_bytes)}
                      </td>
                      <td className="py-2.5 px-3 text-right text-emerald-600 dark:text-emerald-400 font-bold">
                        {item.operations_per_sec !== null && item.operations_per_sec !== undefined
                          ? `${item.operations_per_sec.toFixed(2)}/s`
                          : '—'}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {item.is_system && (
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30 font-sans">
                              System
                            </span>
                          )}
                          {item.is_capped && (
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-purple-50 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-500/30 font-sans">
                              Capped
                            </span>
                          )}
                          {!item.is_system && !item.is_capped && (
                            <span className="px-1.5 py-0.2 rounded text-[9px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 font-sans border border-gray-200 dark:border-transparent">
                              Standard
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="py-8 text-center text-gray-500 dark:text-gray-400 italic">
                    {searchQuery || selectedDb !== 'all' || typeFilter !== 'all'
                      ? 'No collections matching the selected filters.'
                      : 'No MongoDB collection inventory reported for this cluster.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Explanatory Notes Banner */}
      {notes && (
        <div className="bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-xl p-3 text-xs text-gray-600 dark:text-gray-400 space-y-1.5">
          <div className="flex items-center gap-1.5 text-gray-800 dark:text-gray-300 font-semibold text-[11px] uppercase tracking-wider">
            <Info className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
            <span>MongoDB WiredTiger Storage & Rate Telemetry Notes</span>
          </div>
          {notes.sizes && <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">• <strong className="text-gray-800 dark:text-gray-300">Sizes:</strong> {notes.sizes}</p>}
          {notes.activity && <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">• <strong className="text-gray-800 dark:text-gray-300">Activity:</strong> {notes.activity}</p>}
          {notes.commands && <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">• <strong className="text-gray-800 dark:text-gray-300">Commands:</strong> {notes.commands}</p>}
        </div>
      )}

      {/* Collection Detail Modal */}
      {selectedCollectionDetail && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 dark:bg-black/75 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl w-full max-w-xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-5 py-3.5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between bg-gray-50 dark:bg-gray-900/90">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded bg-emerald-50 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-transparent">
                  <Layers className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white font-mono">{selectedCollectionDetail.collection}</h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 font-sans">
                    Database: <span className="text-gray-800 dark:text-gray-200 font-mono font-semibold">{selectedCollectionDetail.database}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedCollectionDetail(null)}
                className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Metrics Grid */}
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-3 bg-gray-50/50 dark:bg-gray-950/40 border-b border-gray-200 dark:border-gray-800 text-xs font-mono">
              <div className="bg-white dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700/50 shadow-sm">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-sans uppercase">Documents</span>
                <div className="text-base font-bold text-gray-900 dark:text-white mt-0.5">
                  {selectedCollectionDetail.documents.toLocaleString()}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700/50 shadow-sm">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-sans uppercase">Data Size (Raw)</span>
                <div className="text-base font-bold text-amber-600 dark:text-amber-400 mt-0.5">
                  {fmtSize(selectedCollectionDetail.data_size_mb)}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700/50 shadow-sm">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-sans uppercase">Disk Storage</span>
                <div className="text-base font-bold text-purple-600 dark:text-purple-400 mt-0.5">
                  {fmtSize(selectedCollectionDetail.storage_size_mb)}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700/50 shadow-sm">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-sans uppercase">Index Size</span>
                <div className="text-base font-bold text-cyan-600 dark:text-cyan-400 mt-0.5">
                  {fmtSize(selectedCollectionDetail.index_size_mb)}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700/50 shadow-sm">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-sans uppercase">Avg Document</span>
                <div className="text-base font-bold text-gray-800 dark:text-gray-200 mt-0.5">
                  {fmtBytes(selectedCollectionDetail.avg_document_size_bytes)}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800/40 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700/50 shadow-sm">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-sans uppercase">Index Count</span>
                <div className="text-base font-bold text-gray-800 dark:text-gray-200 mt-0.5">
                  {selectedCollectionDetail.index_count}
                </div>
              </div>
            </div>

            {/* Operation Rates Detail */}
            <div className="p-4 space-y-2">
              <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider font-sans">
                Real-Time Operations Rates
              </span>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 font-mono text-xs">
                <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400 text-[11px] font-sans">Reads/sec:</span>
                  <div className="text-emerald-600 dark:text-emerald-400 font-bold mt-0.5">{selectedCollectionDetail.reads_per_sec ?? 0}/s</div>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400 text-[11px] font-sans">Inserts/sec:</span>
                  <div className="text-blue-600 dark:text-blue-400 font-bold mt-0.5">{selectedCollectionDetail.inserts_per_sec ?? 0}/s</div>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400 text-[11px] font-sans">Updates/sec:</span>
                  <div className="text-amber-600 dark:text-amber-400 font-bold mt-0.5">{selectedCollectionDetail.updates_per_sec ?? 0}/s</div>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400 text-[11px] font-sans">Deletes/sec:</span>
                  <div className="text-rose-600 dark:text-rose-400 font-bold mt-0.5">{selectedCollectionDetail.deletes_per_sec ?? 0}/s</div>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400 text-[11px] font-sans">Commands/sec:</span>
                  <div className="text-purple-600 dark:text-purple-400 font-bold mt-0.5">{selectedCollectionDetail.commands_per_sec ?? 0}/s</div>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400 text-[11px] font-sans">Total Ops:</span>
                  <div className="text-gray-900 dark:text-white font-bold mt-0.5">{selectedCollectionDetail.operations_per_sec ?? 0}/s</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/90 flex justify-end">
              <button
                onClick={() => setSelectedCollectionDetail(null)}
                className="px-4 py-1.5 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs text-gray-800 dark:text-white font-medium rounded-lg border border-gray-300 dark:border-gray-700 transition-colors cursor-pointer shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MongoCollectionsView2;
